import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  LuBedDouble, LuSofa, LuUtensils, LuCookingPot, LuZap, LuBath, LuMountain, LuWifi, LuShieldCheck, LuWind, LuHeart, LuCheck, LuTv, LuSparkles
} from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';
import { ncbService } from '../services/ncbService';

const ICON_MAP = {
  LuBedDouble, LuSofa, LuUtensils, LuCookingPot, LuZap, LuBath, LuMountain, LuWifi, LuShieldCheck, LuWind, LuTv, LuSparkles
};

const DEFAULT_AMENITIES = [
  { icon: LuBedDouble, title: "King-Sized Bed", subtext: "With single pull-out bed" },
  { icon: LuSofa, title: "Sofa Bed", subtext: "Additional sleeping space" },
  { icon: LuUtensils, title: "Dining Area", subtext: "Dedicated space for meals" },
  { icon: LuCookingPot, title: "Fully Equipped Kitchen", subtext: "With drinking water, oven toaster, microwave, and cooking utensils" },
  { icon: LuZap, title: "Flat Iron", subtext: "Available for guest use" },
  { icon: LuBath, title: "Restroom", subtext: "With heater, bath towels, and toiletries" },
  { icon: LuMountain, title: "Premium Balcony", subtext: "With outdoor seating and scenic views" },
  { icon: LuWifi, title: "High-Speed Wi-Fi", subtext: "Complimentary throughout your stay" },
  { icon: LuShieldCheck, title: "Secure Access", subtext: "Private 3rd floor entrance" },
  { icon: LuWind, title: "Air Conditioning", subtext: "Modern climate control for your comfort" },
  { icon: LuTv, title: "Television", subtext: "Enjoy in-room entertainment during your stay" },
  { icon: LuSparkles, title: "Fresh Linens & Essentials", subtext: "Prepared with clean linens, pillows, and guest essentials" }
];

const Stay = () => {
  const [content, setContent] = useState({ sections: {}, items: {} });

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const sections = await ncbService.getPageSections();
        const items = await ncbService.getPageSectionItems();
        const pageSections = sections.filter(s => s.page_slug === 'stay').reduce((acc, s) => ({ ...acc, [s.section_key]: s }), {});
        const pageItems = items.filter(i => i.page_slug === 'stay').reduce((acc, i) => {
          if (!acc[i.section_key]) acc[i.section_key] = [];
          acc[i.section_key].push(i);
          return acc;
        }, {});
        setContent({ sections: pageSections, items: pageItems });
      } catch (err) {
        console.warn("Backend content failed, using fallbacks");
      }
    };
    fetchContent();
  }, []);

  const getS = (key, fallback) => content.sections[key] || fallback;
  const getI = (key, fallback) => content.items[key] || fallback;

  const hero = getS('hero', { title: "The Stay", subtitle: "Accommodations" });
  const section1 = getS('section_1', { title: "Elegance Meets Comfort", body: "R. VENUE Apartelle offers thoughtfully curated spaces that prioritize your comfort. Enjoy a restful night's sleep on premium mattresses wrapped in soft, high-quality linens. The interior features a harmonious blend of muted sage greens, warm woods, and crisp whites.", image_url: "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvanBlZw==/528531fff021bfcdbd42d3cb1260f42b.jpg" });
  const section2 = getS('section_2', { title: "Generous Living Space", body: "Beyond the bedroom, guests enjoy a spacious open-concept area featuring modern seating, elegant flooring, and large windows that let in abundant natural light. It's the perfect spot to unwind after a day of exploring Lucban.", image_url: "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvanBlZw==/18b115c3a773feffb98a55b211f262ea.JPG" });
  const section3 = getS('section_3', { title: "Balcony & Outdoor Living", body: "Experience the soul of Lucban from our expansive balcony with an outdoor living space. This private sanctuary offers a breathtaking, unobstructed view of Mount Banahaw, while also overlooking the lively streets and the vibrant park at the heart of the town.", image_url: "https://media-manager-c.questera.ai/greta-media/40a492dc43bc618e18a36c285588ccb1d2fe8075e2c19e16c3546447a7641f452c8a03ca0e53bb9227c01b726e9832ee/images/aW1hZ2UvanBlZw==/2754a1c3d2aea8bea9b47a265092a2ba.jpg" });
  const amenities = getI('amenity_items', DEFAULT_AMENITIES);

  return (
    <div className="w-full">
      <section className="pt-40 pb-20 bg-brand-olive text-white text-center">
        <div className="container mx-auto px-6">
          <p className="font-script text-3xl text-brand-cream mb-2">{hero.subtitle}</p>
          <h1 className="font-serif text-4xl md:text-6xl uppercase tracking-[0.2em]">{hero.title}</h1>
        </div>
      </section>

      <section className="py-24 bg-white overflow-hidden">
        <div className="container mx-auto px-6 max-w-6xl space-y-24 md:space-y-40">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/2">
              <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-brand-olive mb-6">{section1.title}</h2>
              <p className="text-brand-olive/80 text-lg leading-relaxed font-light mb-10">{section1.body}</p>
              <div className="flex gap-4">
                <div className="flex-1 border border-brand-olive/20 p-6 text-center bg-brand-cream/30"><p className="text-3xl font-serif text-brand-olive mb-1">2-4</p><p className="text-[10px] font-bold uppercase tracking-widest text-brand-olive/60">Guests</p></div>
                <div className="flex-1 border border-brand-olive/20 p-6 text-center bg-brand-cream/30"><p className="text-3xl font-serif text-brand-olive mb-1">1</p><p className="text-[10px] font-bold uppercase tracking-widest text-brand-olive/60">Bedroom</p></div>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/2 flex justify-center lg:justify-end">
              <div className="bg-brand-cream p-3 shadow-2xl border border-brand-olive/5 w-full max-w-xl"><img src={section1.image_url} alt="" className="w-full aspect-video object-cover" /></div>
            </motion.div>
          </div>

          <div className="w-full h-px bg-brand-olive/10" />

          <div className="flex flex-col lg:flex-row-reverse items-center gap-12 lg:gap-20">
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/2">
              <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-brand-olive mb-6">{section2.title}</h2>
              <p className="text-brand-olive/80 text-lg leading-relaxed font-light mb-8">{section2.body}</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/2 flex justify-center lg:justify-start">
              <div className="bg-brand-cream p-3 shadow-2xl border border-brand-olive/5 w-full max-w-xl"><img src={section2.image_url} alt="" className="w-full aspect-video object-cover" /></div>
            </motion.div>
          </div>

          <div className="w-full h-px bg-brand-olive/10" />

          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/2">
              <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-widest text-brand-olive mb-6">{section3.title}</h2>
              <p className="text-brand-olive/80 text-lg leading-relaxed font-light mb-8">{section3.body}</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="w-full lg:w-1/2 flex justify-center lg:justify-end">
              <div className="bg-brand-cream p-3 shadow-2xl border border-brand-olive/5 w-full max-w-xl"><img src={section3.image_url} alt="" className="w-full aspect-video object-cover" /></div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-brand-cream">
        <div className="container mx-auto px-6 max-w-6xl text-center mb-20">
          <h2 className="font-serif text-3xl md:text-4xl uppercase tracking-[0.15em] text-brand-olive mb-4">Apartelle Amenities</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
            {amenities.map((item, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: idx * 0.05 }} className="flex flex-col items-center text-center p-8 border border-brand-olive/10 bg-white hover:shadow-2xl transition-all rounded-sm group h-full">
                <div className="w-16 h-16 rounded-full bg-brand-cream flex items-center justify-center mb-6 transition-all group-hover:bg-brand-olive"><SafeIcon icon={ICON_MAP[item.icon_name] || item.icon} className="text-brand-olive w-7 h-7 group-hover:text-white" /></div>
                <h3 className="font-serif text-lg uppercase tracking-widest text-brand-olive mb-3">{item.item_title || item.title}</h3>
                <p className="text-brand-olive/60 text-sm font-light leading-relaxed">{item.item_description || item.subtext}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-20 pt-12 border-t border-brand-olive/10 flex flex-col items-center">
            <div className="inline-flex items-center gap-3 px-8 py-4 bg-white rounded-full border border-brand-olive/5 shadow-sm">
              <SafeIcon icon={LuHeart} className="text-brand-olive w-5 h-5" />
              <p className="text-brand-olive/60 text-[10px] font-black uppercase tracking-[0.2em]">Your comfort and safety are our top priority at R. Venue Apartelle.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Stay;